import { useId } from 'react';

import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { Switch } from 'cpool-components/input/Switch';
import { APPLICANT_ATTRIBUTION_BOTH, type APPLICANT_ATTRIBUTION_BOTH_TYPE, MAIL_CONTENT_KINDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import type { InputKind } from '../headerFooterTemplate/types';
import { cn } from '~/utils/cnTailwind';

type Props = {
    canMailSecondApplicant: boolean;
    contentKind: InputKind;
    hasSecondApplicant: boolean;
    onContentKindChange: (contentKind: InputKind) => void;
    onRecipientChange: (recipient: APPLICANT_ATTRIBUTION_BOTH_TYPE) => void;
    recipient: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    secondApplicantName: string;
    templateName?: string | null;
};

export const MailToolbar: React.FC<Props> = ({
    canMailSecondApplicant,
    contentKind,
    hasSecondApplicant,
    onContentKindChange,
    onRecipientChange,
    recipient,
    secondApplicantName,
    templateName,
}) => {
    const { t } = useTranslation();
    const recipientLabelId = useId();
    const ccLabelId = useId();
    const isJointRecipient = recipient === APPLICANT_ATTRIBUTION_BOTH.BEIDE;

    // Both of these deliver the mail to the second applicant - as the recipient, or as the CC of
    // a joint mail - so neither may be picked before that applicant accepted data processing.
    const secondApplicantOptionProps = {
        description: canMailSecondApplicant ? undefined : t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_WARNING'),
        disabled: !canMailSecondApplicant,
    };

    return (
        <div className="grid h-11 shrink-0 grid-cols-[1fr_auto_1fr] items-center bg-surface-light px-[18px] pt-3">
            <span className="justify-self-start text-base text-midnight font-bold">{templateName}</span>
            {hasSecondApplicant && (
                <div className="col-start-2 flex items-center gap-2 text-sm font-medium text-midnight">
                    <span id={recipientLabelId}>{t('MESSAGE.EMAIL.RECIPIENT')}</span>
                    <DropdownMenu<APPLICANT_ATTRIBUTION_BOTH_TYPE>
                        aria-labelledby={recipientLabelId}
                        matchTriggerWidth
                        options={[
                            {
                                label: t('APPLICANT_ATTRIBUTION.ANTRAGSTELLER_1'),
                                value: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            },
                            {
                                ...secondApplicantOptionProps,
                                label: t('APPLICANT_ATTRIBUTION.ANTRAGSTELLER_2'),
                                value: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2,
                            },
                            {
                                ...secondApplicantOptionProps,
                                label: t('APPLICANT_ATTRIBUTION.BEIDE'),
                                value: APPLICANT_ATTRIBUTION_BOTH.BEIDE,
                            },
                        ]}
                        showBorder
                        triggerWidth="150px"
                        value={recipient}
                        onChange={(value) => {
                            if (!Array.isArray(value)) onRecipientChange(value);
                        }}
                    />
                    {/* The Cc field keeps its space even when it is not in use. This group sits in the
                        centred grid column, so mounting the Cc field on demand would widen the group
                        and push the recipient field to the left. Reserving the space instead keeps the
                        recipient field put whichever recipient is picked. `invisible` also takes the
                        hidden field out of the tab order and the accessibility tree. */}
                    <div
                        aria-hidden={!isJointRecipient}
                        className={cn(
                            'flex shrink-0 items-center gap-2',
                            !isJointRecipient && 'invisible pointer-events-none',
                        )}
                        data-testid="email-cc-slot"
                    >
                        <span id={ccLabelId}>{t('MESSAGE.EMAIL.CC')}</span>
                        <DropdownMenu<APPLICANT_ATTRIBUTION_BOTH_TYPE>
                            aria-labelledby={ccLabelId}
                            data-testid="email-cc-recipient"
                            disabled
                            matchTriggerWidth
                            options={[
                                {
                                    label: secondApplicantName,
                                    value: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2,
                                },
                            ]}
                            showBorder
                            triggerWidth="150px"
                            value={APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2}
                        />
                    </div>
                </div>
            )}
            <div className="col-start-3 justify-self-end [&>label]:flex-row-reverse">
                <span className="sr-only">{t('MESSAGE.EMAIL.SR_ONLY_TEXT_HTML_SWITCH')}</span>
                <Switch
                    id="email-html-preview"
                    size="default"
                    className="h-5 w-9 [&>span]:size-4 peer-checked:[&>span]:translate-x-4"
                    label={t('MESSAGE.EMAIL.HTML_CONTENT')}
                    labelClassName="text-sm"
                    checked={contentKind === MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT}
                    onChange={(event) =>
                        onContentKindChange(
                            event.target.checked
                                ? MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT
                                : MAIL_CONTENT_KINDS.MAIL_CONTENT,
                        )
                    }
                />
            </div>
        </div>
    );
};
